import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Input, Modal, Select, Slider } from "antd";

import Title from "antd/es/typography/Title";
import {
  COST_SAVINGS_MARKS,
  FILTER_INITIAL_OBJECT,
} from "../../screens/Rankings/RankingsUtils";
import { RankingAPI } from "../../API/RankingAPI";
import { OptimisationReviewAPI } from "../../API/OptimisationreviewApi";
import { useFlightModel } from "../../contexts/FlightModelContext";

const isOptimisationScope = (scope) => scope === "optimisation";

const toOptionalString = (value) => {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  return trimmed ? trimmed : null;
};

const toCommaList = (values) => {
  if (!Array.isArray(values) || values.length === 0) return null;
  const joined = values
    .map((ele) => String(ele).trim())
    .filter(Boolean)
    .join(",");
  return joined || null;
};

const buildNextFilters = (inputElements) => ({
  cat: inputElements.cat,
  status: inputElements.status,
  task: inputElements.task,
  specType: inputElements.specType,
  yieldValueFrom: inputElements.yieldValueFrom,
  yieldValueTo: inputElements.yieldValueTo,
  maintenanceCheck: inputElements.maintenanceCheck,
  maintenanceCheckPackage: inputElements.maintenanceCheckPackage,
  maintenanceCheckInterval: inputElements.maintenanceCheckInterval,
  maintenanceCheckPackageInterval:
    inputElements.maintenanceCheckPackageInterval,
});

// toOptionalString is used instead of `?.trim() !== ""` so a field cleared via
// the Select's allowClear (which sets undefined, not "") still counts as empty.
const hasActiveFilters = (inputElements, includeYield = true) =>
  toOptionalString(inputElements?.cat) !== null ||
  toOptionalString(inputElements?.specType) !== null ||
  inputElements?.status?.length > 0 ||
  inputElements?.task?.length > 0 ||
  toOptionalString(inputElements?.maintenanceCheck) !== null ||
  toOptionalString(inputElements?.maintenanceCheckPackage) !== null ||
  toOptionalString(inputElements?.maintenanceCheckInterval) !== null ||
  toOptionalString(inputElements?.maintenanceCheckPackageInterval) !== null ||
  (includeYield &&
    (inputElements?.yieldValueFrom !== 0 ||
      inputElements?.yieldValueTo !== 100));

const isArrayEqual = (arr1, arr2) => {
  const a1 = Array.isArray(arr1) ? arr1 : [];
  const a2 = Array.isArray(arr2) ? arr2 : [];
  if (a1.length !== a2.length) return false;
  return a1.every((val, index) => val === a2[index]);
};

const isFiltersDifferent = (a, b) => {
  if (!a || !b) return false;
  return (
    (a.cat ?? "") !== (b.cat ?? "") ||
    (a.specType ?? null) !== (b.specType ?? null) ||
    (a.maintenanceCheck ?? null) !== (b.maintenanceCheck ?? null) ||
    (a.maintenanceCheckPackage ?? null) !== (b.maintenanceCheckPackage ?? null) ||
    (a.maintenanceCheckInterval ?? null) !== (b.maintenanceCheckInterval ?? null) ||
    (a.maintenanceCheckPackageInterval ?? null) !== (b.maintenanceCheckPackageInterval ?? null) ||
    (a.yieldValueFrom ?? 0) !== (b.yieldValueFrom ?? 0) ||
    (a.yieldValueTo ?? 100) !== (b.yieldValueTo ?? 100) ||
    !isArrayEqual(a.status, b.status) ||
    !isArrayEqual(a.task, b.task)
  );
};

const parseFilterRows = (res) => {
  const payload = res?.data?.status !== undefined ? res.data : res;
  const rows = Array.isArray(payload?.data) ? payload.data : [];
  return { ok: payload?.status === true, rows };
};

export const RankingFilters = ({
  open,
  setOpen,
  setListData,
  setDragableSatate,
  setFilterStatus,
  filteredValues,
  setFilteredvalues,
  serachInput,
  setTotalRecords,
  setPage,
  pageSize,
  messageToast,
  scope = "ranking",
  onOptimisationApply,
  onOptimisationClear,
}) => {
  const isOptimisation = isOptimisationScope(scope);
  const { selectedFlightModel, refreshFlightModels } = useFlightModel();
  const [inputElements, setInputElements] = useState(FILTER_INITIAL_OBJECT);
  const yieldContainerRef = useRef(null);
  // Which footer action is in flight ("apply" | "clear" | null) so the spinner
  // shows only on the button that was actually clicked.
  const [pendingAction, setPendingAction] = useState(null);
  const isApplying = pendingAction === "apply";
  const isClearing = pendingAction === "clear";
  // While one footer action runs, the other two are disabled so a second
  // request cannot be fired against a modal that is about to close.
  const isBusy = pendingAction !== null;
  // Apply is enabled if inputs contain active filters, currently applied filters exist,
  // or inputs differ from currently applied filters.
  const canApply = isOptimisation
    ? hasActiveFilters(inputElements, false)
    : hasActiveFilters(inputElements, true) ||
      hasActiveFilters(filteredValues, true) ||
      isFiltersDifferent(inputElements, filteredValues);
  const [filterOptions, setFilterOptions] = useState({
    spec_types: [],
    statuses: [],
    task_types: [],
    mntc_checks: [],
    mntc_check_packages: [],
    mntc_check_intervals: [],
    mntc_check_package_intervals: [],
  });

  // `refreshFlightModels` is rebuilt by its context every time the flight-model
  // list or selection changes. Reading it through a ref keeps resolveFlightModel
  // (and therefore getFilterOptions) stable, so the options effect below reruns
  // only when the selected model actually changes instead of on every context
  // identity change. Callers still see the latest context values.
  const flightModelRef = useRef({ selectedFlightModel, refreshFlightModels });
  useEffect(() => {
    flightModelRef.current = { selectedFlightModel, refreshFlightModels };
  }, [selectedFlightModel, refreshFlightModels]);

  const resolveFlightModel = useCallback(async () => {
    const { selectedFlightModel: currentModel, refreshFlightModels: refresh } =
      flightModelRef.current;

    let flightModelToUse = currentModel;
    if (!flightModelToUse) {
      const refreshed = await refresh();
      flightModelToUse = refreshed?.selectedFlightModel || null;
    }
    return flightModelToUse;
  }, []);

  // The options are per flight model and never change while one is selected, so
  // a repeat request for a model already loaded would return what is on screen.
  const loadedOptionsFlightModelRef = useRef(null);

  const getFilterOptions = useCallback(async () => {
    try {
      const flightModelToUse = await resolveFlightModel();
      if (!flightModelToUse) {
        messageToast?.("error", "No flight model selected.");
        return;
      }

      if (loadedOptionsFlightModelRef.current === flightModelToUse) {
        return;
      }

      // Shared dropdown options from ranking-api (ignore yield buckets for UI).
      const res = await RankingAPI.getFilterFieldOptions({
        params: { flight_model: flightModelToUse },
      });

      if (res?.data) {
        loadedOptionsFlightModelRef.current = flightModelToUse;
        setFilterOptions({
          spec_types: res.data.spec_types || [],
          statuses: res.data.statuses || [],
          task_types: res.data.task_types || [],
          mntc_checks: res.data.mntc_checks || [],
          mntc_check_packages: res.data.mntc_check_packages || [],
          mntc_check_intervals: res.data.mntc_check_intervals || [],
          mntc_check_package_intervals:
            res.data.mntc_check_package_intervals || [],
        });
      }
    } catch {
      messageToast?.("error", "Internal Server Error");
    }
  }, [resolveFlightModel, messageToast]);

  useEffect(() => {
    // selectedFlightModel is an explicit dependency so switching aircraft still
    // reloads the dropdowns now that getFilterOptions itself is stable.
    getFilterOptions();
  }, [getFilterOptions, selectedFlightModel]);

  async function applyOptimisationFilter(flightModelToUse, nextFilters) {
    const bodyData = {
      flight_model: flightModelToUse,
      spec_type: toOptionalString(inputElements.specType),
      status: toCommaList(inputElements.status),
      task_type: toCommaList(inputElements.task),
      fec: toOptionalString(inputElements.cat),
      mntc_check: toOptionalString(inputElements.maintenanceCheck),
      mntc_check_package: toOptionalString(
        inputElements.maintenanceCheckPackage,
      ),
      mntc_check_interval: toOptionalString(
        inputElements.maintenanceCheckInterval,
      ),
      mntc_check_package_interval: toOptionalString(
        inputElements.maintenanceCheckPackageInterval,
      ),
    };

    const response = await OptimisationReviewAPI.filter(bodyData);
    const { ok, rows } = parseFilterRows(response);
    if (!ok) {
      messageToast?.(
        "error",
        response?.statusText || "Failed to apply filters",
      );
      return;
    }

    onOptimisationApply?.(rows, nextFilters);
    setOpen(false);
  }

  async function applyRankingFilter(flightModelToUse, nextFilters) {
    const hasFilter = hasActiveFilters(inputElements, true);
    const queryParams = {
      params: {
        flight_model: flightModelToUse,
        page: 1,
        page_size: pageSize,
        spec_type: inputElements.specType,
        status: inputElements.status.map((ele) => ele.trim()).join(","),
        mntc_check: inputElements.maintenanceCheck,
        mntc_check_package: inputElements.maintenanceCheckPackage,
        mntc_check_interval: inputElements.maintenanceCheckInterval,
        mntc_check_package_interval:
          inputElements.maintenanceCheckPackageInterval,
        yield_min: inputElements.yieldValueFrom,
        yield_max: inputElements.yieldValueTo,
        fec: inputElements.cat,
        task_type: inputElements.task.map((ele) => ele.trim()).join(","),
        search: serachInput?.trim?.() || "",
      },
    };

    const response = await RankingAPI.getRannkList(queryParams);
    if (response?.data?.status !== true) {
      messageToast?.(
        "error",
        response?.statusText || "Failed to apply filters",
      );
      return;
    }

    setTotalRecords(response.data.total);
    setListData(response?.data?.data || []);
    setPage(1);
    setFilteredvalues(nextFilters);

    if (!hasFilter) {
      setDragableSatate(true);
      setFilterStatus(false);
      setOpen(false);
      return;
    }

    setDragableSatate(false);
    setFilterStatus(true);
    setOpen(false);
  }

  async function applyFun() {
    // Optimization Review only: applying an empty filter would replace the
    // review's specifications with an unfiltered fetch, so stop before the API
    // call. The ranking grid keeps its existing "apply nothing = reset" flow.
    if (isOptimisation && !hasActiveFilters(inputElements, false)) {
      messageToast?.("warning", "Select at least one filter to apply.");
      return;
    }

    const flightModelToUse = await resolveFlightModel();
    if (!flightModelToUse) {
      messageToast?.("error", "No flight model selected.");
      return;
    }

    const nextFilters = buildNextFilters(inputElements);

    try {
      setPendingAction("apply");
      if (isOptimisation) {
        await applyOptimisationFilter(flightModelToUse, nextFilters);
      } else {
        await applyRankingFilter(flightModelToUse, nextFilters);
      }
    } catch (error) {
      messageToast?.("error", error?.message || "Failed to apply filters");
    } finally {
      setPendingAction(null);
    }
  }
  const handleFromChange = (e) => {
    const raw = e.target.value.replace(/[^0-9]/g, "");
    const value = raw === "" ? 0 : Math.min(100, Math.max(0, parseInt(raw, 10)));
    setInputElements((prev) => ({
      ...prev,
      yieldValueFrom: value,
    }));
  };

  const handleToChange = (e) => {
    const raw = e.target.value.replace(/[^0-9]/g, "");
    const value = raw === "" ? 0 : Math.min(100, Math.max(0, parseInt(raw, 10)));
    setInputElements((prev) => ({
      ...prev,
      yieldValueTo: value,
    }));
  };

  const handleContainerBlur = (event) => {
    if (
      yieldContainerRef.current &&
      event.relatedTarget &&
      yieldContainerRef.current.contains(event.relatedTarget)
    ) {
      return;
    }

    setInputElements((prev) => {
      const from = prev.yieldValueFrom;
      const to = prev.yieldValueTo;
      if (from > to) {
        return {
          ...prev,
          yieldValueFrom: to,
          yieldValueTo: from,
        };
      }
      return prev;
    });
  };

  const handleSliderChange = (value) => {
    setInputElements((prev) => ({
      ...prev,
      yieldValueFrom: value[0],
      yieldValueTo: value[1],
    }));
  };

  async function cancelFun() {
    if (isOptimisation) {
      // Same pendingAction lifecycle as the ranking scope so Cancel and Apply
      // are disabled for as long as the clear runs, even though this branch has
      // no API call of its own.
      try {
        setPendingAction("clear");
        // Tell the caller whether anything was actually entered, so it can skip
        // a success toast for a Clear All that had nothing to clear. Yield is
        // excluded because the optimisation filter has no yield field.
        const clearedSomething = hasActiveFilters(inputElements, false);
        setInputElements(FILTER_INITIAL_OBJECT);
        await onOptimisationClear?.(FILTER_INITIAL_OBJECT, clearedSomething);
      } catch (error) {
        messageToast?.("error", error?.message || "Failed to clear filters");
      } finally {
        setPendingAction(null);
      }
      // Clear All only resets the fields; the modal stays open so the user can
      // pick new filters. Closing is left to Cancel/Apply.
      return;
    }

    try {
      setPendingAction("clear");
      const flightModelToUse = await resolveFlightModel();
      if (!flightModelToUse) {
        messageToast?.("error", "No flight model selected.");
        return;
      }

      const queryParams = {
        params: {
          flight_model: flightModelToUse,
          page: 1,
          page_size: pageSize,
          spec_type: null,
          status: [],
          mntc_check: null,
          mntc_check_package: null,
          mntc_check_interval: null,
          mntc_check_package_interval: null,
          yield_min: null,
          yield_max: null,
          fec: null,
          task_type: [],
          search: serachInput?.trim?.() || "",
        },
      };
      const res = await RankingAPI.getRannkList(queryParams);

      if (res?.data?.status === true) {
        setListData(res?.data?.data || []);
        setTotalRecords(res.data.total);
        setPage(1);

        if (!serachInput?.trim?.()) {
          setDragableSatate(true);
        } else {
          setDragableSatate(false);
        }
        // Fields and the grid behind the modal are reset, but the modal itself
        // stays open so the user can keep filtering.
        setInputElements(FILTER_INITIAL_OBJECT);
        setFilterStatus(false);
        setFilteredvalues(FILTER_INITIAL_OBJECT);
      } else {
        messageToast?.("error", res?.statusText || "Failed to clear filters");
      }
    } catch (error) {
      messageToast?.("error", error?.message || "Failed to clear filters");
    } finally {
      setPendingAction(null);
    }
  }

  function backFun() {
    setInputElements(FILTER_INITIAL_OBJECT);
    setOpen(false);
  }

  useEffect(() => {
    if (open) {
      setInputElements({
        cat: filteredValues.cat,
        status: filteredValues.status,
        task: filteredValues.task,
        specType: filteredValues.specType,
        yieldValueFrom: filteredValues.yieldValueFrom,
        yieldValueTo: filteredValues.yieldValueTo,
        maintenanceCheck: filteredValues.maintenanceCheck,
        maintenanceCheckPackage: filteredValues.maintenanceCheckPackage,
        maintenanceCheckInterval: filteredValues.maintenanceCheckInterval,
        maintenanceCheckPackageInterval:
          filteredValues.maintenanceCheckPackageInterval,
      });
    }
  }, [open, filteredValues]);

  return (
    <Modal
      open={open}
      width="900px"
      zIndex={2000}
      okText="Apply"
      cancelText="Clear All"
      onCancel={() => backFun()}
      onOk={applyFun}
      footer={[
        <Button key="back" type="text" onClick={backFun} disabled={isBusy}>
          Cancel
        </Button>,
        <Button
          key="clear"
          type="default"
          onClick={cancelFun}
          loading={isClearing}
          disabled={isApplying}
          // Fixed width: antd renders the loading spinner inside the button, so
          // an auto-width button grows when it starts loading and shifts the
          // rest of the right-aligned footer.
          style={{ width: "110px" }}
        >
          Clear All
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={applyFun}
          loading={isApplying}
          disabled={isClearing || !canApply}
          style={{ width: "100px" }}
        >
          Apply
        </Button>,
      ]}
    >
      <div style={{ padding: "10px" }}>
        <div
          style={{ marginBottom: "20px", borderBottom: "solid 1px #80808047" }}
        >
          <Title level={5}>Filters</Title>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "15px",
          }}
        >
          <div>
            <label>FEC</label>
            <Input
              value={inputElements.cat}
              onChange={(e) => {
                const value = e.target.value.trimStart();
                setInputElements((prev) => ({
                  ...prev,
                  cat: value,
                }));
              }}
            />
          </div>

          <div>
            <label>Status</label>
            <Select
              mode="multiple"
              allowClear
              style={{
                background: "#EBEEF8",
                fontSize: "13px",
                display: "block",
              }}
              value={inputElements.status}
              onChange={(e) => {
                setInputElements((prev) => ({
                  ...prev,
                  status: e,
                }));
              }}
              options={filterOptions.statuses.map((s) => ({
                value: s,
                label: s,
              }))}
            />
          </div>

          <div>
            <label>Task Type</label>
            <Select
              mode="multiple"
              allowClear
              style={{
                background: "#EBEEF8",
                fontSize: "13px",
                display: "block",
              }}
              value={inputElements.task}
              onChange={(e) => {
                setInputElements((prev) => ({
                  ...prev,
                  task: e,
                }));
              }}
              options={filterOptions.task_types.map((ele) => ({
                value: ele,
                label: ele,
              }))}
            />
          </div>

          <div>
            <label>Maintenance Check</label>
            <Select
              // allowClear
              style={{
                background: "#EBEEF8",
                fontSize: "13px",
                display: "block",
              }}
              value={inputElements.maintenanceCheck}
              onChange={(value) => {
                setInputElements((prev) => ({
                  ...prev,
                  maintenanceCheck: value,
                }));
              }}
              options={filterOptions.mntc_checks.map((v) => ({
                value: v,
                label: v,
              }))}
            />
          </div>

          <div>
            <label>Maintenance Check Package</label>
            <Select
              // allowClear
              style={{
                background: "#EBEEF8",
                fontSize: "13px",
                display: "block",
              }}
              value={inputElements.maintenanceCheckPackage}
              onChange={(value) => {
                setInputElements((prev) => ({
                  ...prev,
                  maintenanceCheckPackage: value,
                }));
              }}
              options={filterOptions.mntc_check_packages.map((v) => ({
                value: v,
                label: v,
              }))}
            />
          </div>

          <div>
            <label>Maintenance Check Interval</label>
            <Select
              // allowClear
              style={{
                background: "#EBEEF8",
                fontSize: "13px",
                display: "block",
              }}
              value={inputElements.maintenanceCheckInterval}
              onChange={(value) => {
                setInputElements((prev) => ({
                  ...prev,
                  maintenanceCheckInterval: value,
                }));
              }}
              options={filterOptions.mntc_check_intervals.map((v) => ({
                value: v,
                label: v,
              }))}
            />
          </div>

          <div>
            <label>Maintenance Check Package Interval</label>
            <Select
              // allowClear
              style={{
                background: "#EBEEF8",
                fontSize: "13px",
                display: "block",
              }}
              value={inputElements.maintenanceCheckPackageInterval}
              onChange={(value) => {
                setInputElements((prev) => ({
                  ...prev,
                  maintenanceCheckPackageInterval: value,
                }));
              }}
              options={filterOptions.mntc_check_package_intervals.map((v) => ({
                value: v,
                label: v,
              }))}
            />
          </div>

          <div>
            <label>Spec Type</label>
            <Select
              allowClear
              style={{
                background: "#EBEEF8",
                fontSize: "13px",
                display: "block",
              }}
              value={inputElements.specType}
              onChange={(e) => {
                setInputElements((prev) => ({
                  ...prev,
                  specType: e,
                }));
              }}
              options={filterOptions.spec_types.map((v) => ({
                value: v,
                label: v,
              }))}
            />
          </div>

          {/* Optimization Review filter API does not support yield */}
          {!isOptimisation ? (
            <div>
              <label>Yield Percentage</label>
              <div
                ref={yieldContainerRef}
                onBlur={handleContainerBlur}
                style={{ display: "flex", gap: "10px", marginBottom: "10px" }}
              >
                <Input
                  value={inputElements.yieldValueFrom}
                  onChange={handleFromChange}
                  style={{ width: "80px" }}
                />
                <Input
                  value={inputElements.yieldValueTo}
                  onChange={handleToChange}
                  style={{ width: "80px" }}
                />
              </div>
              <Slider
                range
                min={0}
                max={100}
                marks={COST_SAVINGS_MARKS}
                value={[
                  inputElements.yieldValueFrom,
                  inputElements.yieldValueTo,
                ]}
                onChange={handleSliderChange}
              />
            </div>
          ) : null}
        </div>
      </div>
    </Modal>
  );
};
